import {
  type BoardElement,
  type ConnectorEndpoint,
  createElement,
  ELEMENT_ENUMS as ENUMS,
  isElementType,
  ELEMENT_SIZES as SIZES,
} from './elements';
import { clamp } from './math';

const MAX_TEXT = 20_000;
const MAX_STRING = 200;
const MAX_ID = 64;
const MAX_COORDINATE = 1e7;

function finite(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, -MAX_COORDINATE, MAX_COORDINATE)
    : fallback;
}

function endpoint(value: unknown, fallback: ConnectorEndpoint): ConnectorEndpoint {
  if (typeof value !== 'object' || value === null) return { ...fallback };
  const v = value as Record<string, unknown>;
  const a = v.anchor as Record<string, unknown> | null | undefined;
  return {
    x: finite(v.x, fallback.x),
    y: finite(v.y, fallback.y),
    bindId: typeof v.bindId === 'string' ? v.bindId.slice(0, MAX_ID) : null,
    anchor:
      a && typeof a === 'object'
        ? { x: clamp(finite(a.x, 0.5), 0, 1), y: clamp(finite(a.y, 0.5), 0, 1) }
        : null,
  };
}

function votes(value: unknown): Record<string, true> {
  const out: Record<string, true> = {};
  if (typeof value !== 'object' || value === null) return out;
  for (const [voter, cast] of Object.entries(value)) {
    if (cast === true && voter.length <= MAX_ID) out[voter] = true;
  }
  return out;
}

/**
 * Rebuilds an element from untrusted data (an imported file, the clipboard):
 * every known property is coerced to its expected type or replaced by its
 * default, strings are length-limited, and unknown properties are dropped.
 * Returns null if the data is not an element at all.
 */
export function sanitizeElement(raw: unknown): BoardElement | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const input = raw as Record<string, unknown>;
  if (!isElementType(input.type)) return null;
  if (typeof input.id !== 'string' || typeof input.frameId !== 'string') return null;
  if (!input.id || !input.frameId) return null;

  const defaults = createElement(input.type, { frameId: '' }) as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, fallback] of Object.entries(defaults)) {
    const value = input[key];
    if (key === 'type') out.type = input.type;
    else if (key === 'id' || key === 'frameId') out[key] = (value as string).slice(0, MAX_ID);
    else if (key in ENUMS) out[key] = ENUMS[key]?.includes(value as string) ? value : fallback;
    else if (key === 'points') out.points = value instanceof Uint8Array ? value : fallback;
    else if (key === 'votes') out.votes = votes(value);
    else if (key === 'start' || key === 'end') {
      out[key] = endpoint(value, fallback as ConnectorEndpoint);
    } else if (key === 'text') out.text = typeof value === 'string' ? value.slice(0, MAX_TEXT) : '';
    else if (key === 'groupId' || key === 'createdBy') {
      out[key] = typeof value === 'string' ? value.slice(0, MAX_ID) : null;
    } else if (key === 'fontSize' && fallback === null) {
      // Sticky notes: null means "fit the text automatically".
      out.fontSize = typeof value === 'number' && Number.isFinite(value) ? value : null;
    } else if (typeof fallback === 'number') out[key] = finite(value, fallback);
    else if (typeof fallback === 'boolean') {
      out[key] = typeof value === 'boolean' ? value : fallback;
    } else if (typeof fallback === 'string') {
      out[key] = typeof value === 'string' ? value.slice(0, MAX_STRING) : fallback;
    } else out[key] = fallback;
  }
  for (const key of SIZES) {
    if (typeof out[key] === 'number') out[key] = clamp(out[key] as number, 1, 100_000);
  }
  return out as unknown as BoardElement;
}
